/**
 * DateRoller — 3-column dropdown (Month · Day · Year) that replaces the
 * native `<input type="date">` calendar picker.  Emits ISO `YYYY-MM-DD`
 * strings on `onChange`; emits an empty string until ALL three fields
 * are chosen so the parent's required-field validator works cleanly.
 *
 * Design notes:
 *  • Local state holds the in-progress selection so partial picks don't
 *    disappear the moment the user changes one column.
 *  • Day dropdown is auto-clamped to the correct number of days for the
 *    picked month/year (leap-year aware).
 *  • Year range defaults to current-year ± 10.
 */
import { useEffect, useMemo, useState } from "react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function daysInMonth(year, monthIdx) {
  // monthIdx is 0-based (0 = Jan).  Rolling over to day 0 of next month
  // gives us the last day of the target month — leap-year safe.
  return new Date(year, monthIdx + 1, 0).getDate();
}

function parseIso(iso) {
  const parts = (iso || "").split("-");
  return {
    y: parts[0] ? parseInt(parts[0], 10) : "",
    m: parts[1] ? parseInt(parts[1], 10) : "",
    d: parts[2] ? parseInt(parts[2], 10) : "",
  };
}

export default function DateRoller({
  value = "",
  onChange,
  yearsBack = 10,
  yearsAhead = 10,
  testId,
}) {
  // Local pending selection — lets the user pick columns one at a time
  // without losing them.  Kept in sync with the parent's ISO value.
  const [local, setLocal] = useState(parseIso(value));
  useEffect(() => { setLocal(parseIso(value)); }, [value]);

  const now = new Date();
  const years = useMemo(() => {
    const start = now.getFullYear() - yearsBack;
    const end = now.getFullYear() + yearsAhead;
    const arr = [];
    for (let i = end; i >= start; i--) arr.push(i);
    return arr;
  }, [now, yearsBack, yearsAhead]);

  const maxDay = useMemo(() => {
    if (!local.y || !local.m) return 31;
    return daysInMonth(Number(local.y), Number(local.m) - 1);
  }, [local.y, local.m]);

  const days = useMemo(() => {
    const arr = [];
    for (let i = 1; i <= maxDay; i++) arr.push(i);
    return arr;
  }, [maxDay]);

  const commit = (next) => {
    setLocal(next);
    if (!next.y || !next.m || !next.d) { onChange(""); return; }
    // Clamp the day so switching to a shorter month doesn't yield an
    // invalid ISO string like 2026-02-30.
    const clampedDay = Math.min(Number(next.d), daysInMonth(Number(next.y), Number(next.m) - 1));
    onChange(
      `${String(next.y).padStart(4, "0")}-${String(next.m).padStart(2, "0")}-${String(clampedDay).padStart(2, "0")}`
    );
  };

  const onMonth = (e) => commit({ ...local, m: e.target.value ? parseInt(e.target.value, 10) : "" });
  const onDay   = (e) => commit({ ...local, d: e.target.value ? parseInt(e.target.value, 10) : "" });
  const onYear  = (e) => commit({ ...local, y: e.target.value ? parseInt(e.target.value, 10) : "" });

  const selectCls = "px-2 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 cursor-pointer";

  return (
    <div className="grid grid-cols-3 gap-2" data-testid={testId}>
      <select
        value={local.m || ""}
        onChange={onMonth}
        className={selectCls}
        data-testid={testId ? `${testId}-month` : undefined}
        aria-label="Month"
      >
        <option value="">Month</option>
        {MONTHS.map((label, i) => (
          <option key={i + 1} value={i + 1}>{label}</option>
        ))}
      </select>
      <select
        value={local.d && local.d <= maxDay ? local.d : ""}
        onChange={onDay}
        className={selectCls}
        data-testid={testId ? `${testId}-day` : undefined}
        aria-label="Day"
      >
        <option value="">Day</option>
        {days.map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
      <select
        value={local.y || ""}
        onChange={onYear}
        className={selectCls}
        data-testid={testId ? `${testId}-year` : undefined}
        aria-label="Year"
      >
        <option value="">Year</option>
        {years.map((yr) => (
          <option key={yr} value={yr}>{yr}</option>
        ))}
      </select>
    </div>
  );
}
